import { StationAvatar } from '@app/components/base/stations/StationAvatar';
import { Station } from '@app/core/types/stations';
import { Autocomplete, Chip, CircularProgress, InputAdornment, TextField } from '@mui/material';
import { HTMLAttributes, Key } from 'react';
import { useIntl } from 'react-intl';
import { stationLabelMessage } from './station-messages';
import { StationsAutocompleteOption } from './StationsAutocompleteOption';

type BaseProps = {
  stations: readonly Station[];
  /** Field label. Pass `false` to render without a label. */
  label?: string | false;
  /** Hint shown in the input when empty (and, in multi mode, when no stations are selected). */
  placeholder?: string;
  /** Helper text rendered below the field. */
  helperText?: string;
  disabled?: boolean;
  /** Shows a spinner in the input end adornment (e.g. while a link request is in flight). */
  loading?: boolean;
  size?: 'small' | 'medium';
};

type SingleProps = BaseProps & {
  multiple?: false;
  value?: Station | null;
  onChange: (station: Station | null) => void;
};

type MultipleProps = BaseProps & {
  multiple: true;
  value?: readonly Station[];
  onChange: (stations: Station[]) => void;
};

type Props = SingleProps | MultipleProps;

/**
 * Searchable station picker (single or multi).
 *
 * Each option shows the station logo next to its name. Multi mode renders selected
 * stations as deletable chips with avatars.
 *
 * In single mode the field width is derived from the longest option name (dropdown is
 * portaled, so CSS alone cannot size the input from options). Multi mode uses full width.
 */
export function StationsAutocomplete(props: Props) {
  const {
    stations,
    label,
    placeholder,
    helperText,
    disabled,
    loading = false,
    size = 'small',
    multiple = false,
  } = props;
  const { $t } = useIntl();
  const fieldLabel = label === false ? undefined : (label ?? $t(stationLabelMessage));
  const longestLabelLength = Math.max(
    fieldLabel?.length ?? 0,
    ...stations.map((station) => station.name.length),
  );
  // Extra characters leave room for the logo, dropdown arrow and input padding.
  const widthInCharacters = Math.min(Math.max(longestLabelLength + 9, 24), 40);

  const renderOption = (
    optionProps: HTMLAttributes<HTMLLIElement> & { key?: Key },
    station: Station,
  ) => {
    const { key, ...liProps } = optionProps;
    return <StationsAutocompleteOption key={key} station={station} liProps={liProps} />;
  };

  if (multiple) {
    const { value = [], onChange } = props as MultipleProps;
    const chipAvatarSize = size === 'medium' ? 24 : 20;

    return (
      <Autocomplete
        multiple
        size={size}
        sx={{ width: '100%', maxWidth: '100%' }}
        options={[...stations]}
        getOptionLabel={(option) => option.name}
        disabled={disabled || stations.length === 0}
        loading={loading}
        value={[...value]}
        onChange={(_event, nextStations) => onChange(nextStations)}
        renderOption={renderOption}
        renderTags={(selected, getTagProps) =>
          selected.map((station, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            return (
              <Chip
                key={key}
                label={station.name}
                size={size}
                variant="outlined"
                avatar={
                  <StationAvatar stationId={station.id} station={station} size={chipAvatarSize} />
                }
                {...tagProps}
              />
            );
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            size={size}
            label={fieldLabel}
            placeholder={placeholder}
            helperText={helperText}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        isOptionEqualToValue={(a, b) => a.id === b.id}
      />
    );
  }

  const { value = null, onChange } = props as SingleProps;

  return (
    <Autocomplete
      sx={{ width: `${widthInCharacters}ch`, maxWidth: '100%' }}
      options={[...stations]}
      getOptionLabel={(option) => option.name}
      disabled={disabled || stations.length === 0}
      loading={loading}
      value={value}
      onChange={(_event, station) => onChange(station)}
      renderOption={renderOption}
      renderInput={(params) => (
        <TextField
          {...params}
          size={size}
          label={fieldLabel}
          placeholder={placeholder}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            startAdornment: value ? (
              <InputAdornment position="start">
                <StationAvatar stationId={value.id} station={value} size={20} />
              </InputAdornment>
            ) : null,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      isOptionEqualToValue={(a, b) => a.id === b.id}
    />
  );
}
